import React from "react";
import { Mic, X, AlertTriangle } from "lucide-react";

const STORAGE_KEY = "tt_voice_consent_v1";

/**
 * Australian recording-laws disclaimer shown ONCE before a user records
 * their first voice note. We don't pretend to give legal advice. plain-
 * English summary so the user knows what they're doing, plus our liability
 * carve-out. Stored in localStorage so we don't nag.
 */
export function hasVoiceConsent() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch (e) { return false; }
}

export function markVoiceConsent() {
  try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
}

export default function VoiceConsentModal({ onAccept, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="voice-consent-modal"
    >
      <div
        className="bg-[#FAF9F6] border border-[#E5E4DE] rounded-sm max-w-lg w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E5E4DE]">
          <div className="flex items-center gap-2">
            <Mic size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
            <h3 className="font-serif text-xl text-[#0D2B52]">Before you record</h3>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost p-1.5"
            data-testid="voice-consent-close"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm leading-relaxed text-[#333]">
          <p>
            Voice notes are a powerful evidence tool. Before you press record,
            here&apos;s what Australian law generally says about recording conversations.
          </p>

          <div className="bg-white border border-[#E5E4DE] p-4 space-y-3">
            <div className="label-mono text-[#0D2B52]">The short version (per state)</div>
            <ul className="space-y-1.5 text-[13px] text-[#333]">
              <li><span className="font-mono text-[#0D2B52]">QLD</span> · One-party consent is OK: you can record a conversation you&apos;re part of.</li>
              <li><span className="font-mono text-[#0D2B52]">VIC</span> · Generally one-party consent for private conversations.</li>
              <li><span className="font-mono text-[#0D2B52]">NT</span> · One-party consent is OK.</li>
              <li><span className="font-mono text-[#0D2B52]">NSW · SA · WA · ACT · TAS</span> · Stricter: all-party consent is usually required for private conversations, with limited &ldquo;protect lawful interests&rdquo; exceptions.</li>
            </ul>
            <p className="text-xs text-[#6B7280] italic">
              Recording someone in your own home when they&apos;ve entered without notice
              is a different conversation from secretly recording a meeting. Different
              states draw the line differently.
            </p>
          </div>

          <div className="bg-[#FFF4EC] border-l-4 border-[#B35D4C] p-3 flex gap-2">
            <AlertTriangle size={16} strokeWidth={1.6} className="text-[#B35D4C] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#333]">
              <span className="font-semibold">TenantTrail does not give legal advice and takes no responsibility for how recordings are used.</span>
              {" "}If you&apos;re unsure whether you&apos;re allowed to record, check with your
              state Tenants&apos; Union or a community legal centre first.
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
            <button
              onClick={onClose}
              className="btn-secondary text-sm"
              data-testid="voice-consent-cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => { markVoiceConsent(); onAccept?.(); }}
              className="btn-primary text-sm"
              data-testid="voice-consent-accept"
            >
              I understand. Start recording
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
